import React, { FC } from 'react';
import { usePostComposer } from 'app/modules/posts/modules/post/components/composer/PostComposerProvider';
import { PostModalOnClose } from 'app/modules/posts/modules/post/hooks/usePostModal';
import { faArrowDownSquareTriangle } from '@fortawesome/pro-regular-svg-icons/faArrowDownSquareTriangle';
import { faClose } from '@fortawesome/pro-regular-svg-icons/faClose';
import { useWidgetsSetup } from 'components/posts/detail/layout/widgetsSetup/WidgetsSetupProvider';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  BrandLogo,
  Icon,
  IconButton,
  Toggle,
  Tooltip,
} from '@kontentino/ui';
import DemoBadge from 'app/components/DemoBadge';
import getSocialMediaTypeForBrandLogo from 'app/utils/getSocialMediaTypeForBrandLogo';
import { faGlobe } from '@fortawesome/pro-regular-svg-icons/faGlobe';
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons/faChevronRight';
import { getYtLogoGuidelineSize } from 'app/utils/getytlogoguidelinesize';
import { faSparkles } from '@fortawesome/pro-regular-svg-icons/faSparkles';
import { useLegacyPostExperienceSwitch } from 'app/modules/posts/modules/post/hooks/useLegacyPostExperienceSwitch';

type Props = {
  onClose: PostModalOnClose;
};

const Header: FC<Props> = ({ onClose }) => {
  const { t } = useTranslation();
  const { isEditing, form } = usePostComposer();
  const { setSetupModalOpen, canSetupWidgets } = useWidgetsSetup();
  const { switchExperience } = useLegacyPostExperienceSwitch();

  return (
    <div className="tw-flex tw-items-center tw-gap-2 tw-px-6 tw-py-4 tw-text-base tw-font-semibold">
      <BrandLogo
        src={form.page.logo?.src}
        name={form.page.name}
        size={28}
        socialMediaType={getSocialMediaTypeForBrandLogo(form.page.type)}
        iconSize={getYtLogoGuidelineSize(12, form.page.type)}
      />
      {form.page.isGlobal && (
        <Icon icon={faGlobe} className="tw-text-grayscale-100" size="lg" />
      )}
      <div className="tw-max-w-xl tw-truncate" translate="no">
        {form.page.name}
      </div>
      {form.page.isDemo && (
        <div className="tw-ml-1 tw-flex">
          <DemoBadge />
        </div>
      )}
      <Icon icon={faChevronRight} className="tw-text-grayscale-100" />

      <div className="tw-mr-2 tw-whitespace-nowrap">
        {isEditing ? t('editPost') : t('createPost')}
      </div>
      <div className="tw-ml-auto tw-flex tw-items-center tw-gap-3">
        <Tooltip
          content={t('postExperienceSwitch.tooltip', {
            defaultValue: 'Switch to the new post experience',
          })}
        >
          <div className="tw-flex tw-items-center tw-gap-2">
            <Icon icon={faSparkles} className="tw-text-grayscale-100" />
            <span className="tw-font-normal tw-whitespace-nowrap tw-text-sm tw-text-grayscale-140">
              {t('layoutFilter.newPostExperience')}
            </span>
            <Badge>Beta</Badge>
            <Toggle
              size="small"
              checked={false}
              onChange={() => switchExperience()}
              data-cy="post-composer-header_post-experience-switch"
            />
          </div>
        </Tooltip>
        {isEditing && canSetupWidgets && (
          <Tooltip content={t('widgetsSetup.modal.title')}>
            <div>
              <IconButton
                variant="secondary"
                size="small"
                onClick={setSetupModalOpen.toggle}
                icon={<Icon icon={faArrowDownSquareTriangle} />}
              />
            </div>
          </Tooltip>
        )}
        <IconButton
          variant="secondary"
          size="small"
          onClick={() => onClose()}
          icon={<Icon icon={faClose} />}
        />
      </div>
    </div>
  );
};

export default Header;
